import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Input,
  Button,
  Space,
  Radio,
  Card,
  Tag,
  Tooltip,
  Empty,
  Spin,
  message,
  Select,
  Row,
  Col,
  Form,
  Checkbox,
} from 'antd';
import { SearchOutlined, DeleteOutlined, PlusOutlined, FilterOutlined, ReloadOutlined, ClearOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Store, StoreSelectionProps } from '../../../types/storeGroup';
import StoreGroupService from '../../../services/storeGroupService';
import { warehouseService } from '../../../services/warehouseService';

const StoreSelection: React.FC<StoreSelectionProps> = ({
  selectedStores,
  onStoreSelect,
  typicalStoreCode,
  onTypicalStoreSelect,
}) => {
  const { t } = useTranslation();
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Store[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  
  // Add filter condition state - support multiple selection
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  
  // Batch selection related state
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Store type options - will be dynamically updated
  const [storeTypeOptions, setStoreTypeOptions] = useState<Array<{ value: string; label: string }>>([
    { value: '', label: t('common.all') }
  ]);

  const storeGroupService = StoreGroupService;

  // Helper function to convert warehouse data to Store format
  const convertWarehouseToStore = (warehouse: any): Store => {
    return {
      id: warehouse.id ? warehouse.id.toString() : '',
      code: warehouse.code || '',
      englishName: warehouse.englishName || '',
      name: warehouse.name || '',
      detailAddress: warehouse.detailAddress || warehouse.address || '',
      area: warehouse.area || warehouse.region || '',
      city: warehouse.city || '',
      format: warehouse.format || warehouse.type || 'Standard',
      clusterSection: warehouse.clusterSection || '',
      size: warehouse.size || 0,
      contact: warehouse.contactName || warehouse.contact || '',
      phone: warehouse.contactPhone || warehouse.phone || '',
      status: warehouse.status === 'ACTIVE' ? 'Active' : 
              warehouse.status === 'INACTIVE' ? 'Inactive' : 
              warehouse.status || 'Active'
    };
  };

  // Store status options
  const STORE_STATUS_OPTIONS = [
    { value: 'Active', label: t('storeSelection.statusActive') },
    { value: 'Inactive', label: t('storeSelection.statusInactive') }
  ];

  // Store type options will be dynamically populated from API data
  // const STORE_TYPE_OPTIONS = [
  //   { value: '', label: t('common.all') },
  //   { value: 'flagship', label: t('storeSelection.flagship') },
  //   { value: 'center', label: t('storeSelection.center') },
  //   { value: 'standard', label: t('storeSelection.standard') }
  // ];

  const handleSearch = async () => {
    // Component initial load allows unconditional query
    // When user manually clicks search, if there are no conditions, prompt user

    setSearching(true);
    try {
      // Use warehouseService.listWarehouse method for search
      const searchReq = {
        searchStr: searchKeyword || '', // If no keyword, pass empty string
        formats: typeFilter,
        statusFilter: statusFilter,
      };
      
      const response = await warehouseService.listWarehouse(searchReq);
      
      if (response.success && response.data) {
        // Convert data format
        let filteredStores = response.data.map(convertWarehouseToStore);
        
        // Apply additional filter conditions
        if (statusFilter.length > 0) {
          filteredStores = filteredStores.filter(store => {
            const storeStatus = store.status || 'Active';
            return statusFilter.includes(storeStatus);
          });
        }
        
        if (typeFilter.length > 0) {
          filteredStores = filteredStores.filter(store => {
            return typeFilter.includes(store.format);
          });
        }
        
        // Filter out already selected stores
        const finalFilteredStores = filteredStores.filter(
          (store) => !selectedStores.some((selected) => selected.id === store.id)
        );
        setSearchResults(finalFilteredStores);
      } else {
        setSearchResults([]);
        message.error(response.errorMessage || t('storeSelection.searchFailed'));
      }
    } catch (error) {
      message.error(t('storeSelection.searchFailed'));
      console.error('Failed to search stores:', error);
    } finally {
      setSearching(false);
    }
  };

  // Auto-execute query when component loads
  useEffect(() => {
    // Execute query once on initial load
    handleSearch();
    
    // Fetch unique formats from warehouseService and update storeTypeOptions
    const fetchStoreFormats = async () => {
      try {
        const response = await warehouseService.listWarehouse({ searchStr: '' });
        if (response.success && response.data) {
          // Extract unique formats from the data
          const formats = response.data.map((item: any) => item.format || item.type || 'Standard');
          const uniqueFormats = Array.from(new Set(formats)) as string[];
          
          // Create options array with unique formats
          const formatOptions = uniqueFormats.map(format => ({
            value: format,
            label: format.charAt(0) + format.slice(1)
          }));
          
          // Update storeTypeOptions with the new options, keeping 'All' at the beginning
          setStoreTypeOptions([
            { value: '', label: t('common.all') },
            ...formatOptions
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch store formats:', error);
        // Fallback to default options if API fails
        setStoreTypeOptions([
          { value: '', label: t('common.all') },
          { value: 'flagship', label: t('storeSelection.flagship') },
          { value: 'center', label: t('storeSelection.center') },
          { value: 'standard', label: t('storeSelection.standard') }
        ]);
      }
    };
    
    fetchStoreFormats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures execution only when component mounts

  // Auto re-search when filter conditions change
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]); // Monitor changes in filter conditions

  const handleAddStore = (store: Store) => {
    const newSelectedStores = [...selectedStores, store];
    onStoreSelect(newSelectedStores);
    // Remove added store from search results
    setSearchResults(searchResults.filter((item) => item.id !== store.id));
    // Clear selection state
    setSelectedRowKeys(selectedRowKeys.filter(key => key !== store.id));
  };
  
  // Batch add selected stores
  const handleBatchAddStores = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('storeSelection.selectStoresFirst'));
      return;
    }
    
    const storesToAdd = searchResults.filter(store => selectedRowKeys.includes(store.id));
    const newSelectedStores = [...selectedStores, ...storesToAdd];
    onStoreSelect(newSelectedStores);
    
    // Remove added stores from search results
    setSearchResults(searchResults.filter(item => !selectedRowKeys.includes(item.id)));
    
    // Clear selection state
    setSelectedRowKeys([]);
    
    message.success(t('storeSelection.addSuccess', { count: storesToAdd.length }));
  };

  const handleRemoveStore = (storeId: string) => {
    const newSelectedStores = selectedStores.filter((store) => store.id !== storeId);
    onStoreSelect(newSelectedStores);
    
    // If the removed store is the representative store, clear representative store selection
    if (storeId === typicalStoreCode) {
      onTypicalStoreSelect('');
    }
  };

  const handleSetRepresentative = (storeId: string) => {
    onTypicalStoreSelect(storeId);
  };

  // Reset all search conditions
  const handleResetFilters = () => {
    setSearchKeyword('');
    setStatusFilter([]);
    setTypeFilter([]);
    setSelectedRowKeys([]);
  };
  
  const searchResultColumns: ColumnsType<Store> = [
    {
      title: t('storeSelection.storeCode'),
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: t('storeSelection.storeName'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: t('warehouse.englishName'),
      dataIndex: 'englishName',
      key: 'englishName',
      width: 200,
    },
     {
      title: t('warehouse.status'),
      dataIndex: 'status',
      key: 'status',
      width: 200,
    },
    {
      title: t('storeSelection.format'),
      dataIndex: 'format',
      key: 'format',
      width: 120,
    },
    {
      title: t('storeSelection.clusterSection'),
      dataIndex: 'clusterSection',
      key: 'clusterSection',
      width: 120,
    },
    
    {
      title: t('storeSelection.city'),
      dataIndex: 'city',
      key: 'city',
      width: 120,
    },
    {
      title: t('common.actions'),
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => handleAddStore(record)}
        >
          {t('storeSelection.add')}
        </Button>
      ),
    },
  ];

  const selectedStoresColumns: ColumnsType<Store> = [
    {
      title: t('storeSelection.storeCode'),
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: t('storeSelection.storeName'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: t('warehouse.englishName'),
      dataIndex: 'englishName',
      key: 'englishName',
      width: 200,
    },
    {
      title: t('storeSelection.city'),
      dataIndex: 'city',
      key: 'city',
      width: 120,
    },
    {
      title: t('storeSelection.representativeStore'),
      key: 'representative',
      width: 100,
      render: (_, record) => (
        <Radio
          checked={record.code === typicalStoreCode}
          onChange={() => handleSetRepresentative(record.code)}
        />
      ),
    },
    {
      title: t('common.actions'),
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Tooltip title={t('storeSelection.remove')}>
          <Button
            danger
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveStore(record.id)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="store-selection">
      <Card title={t('storeSelection.title')}>
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={10}>
              <Form.Item label={t('storeSelection.keywordSearch')}>
                <Input
                  placeholder={t('storeSelection.searchPlaceholder')}
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onPressEnter={handleSearch}
                  allowClear
                  suffix={
                    searchKeyword ? (
                      <ClearOutlined onClick={() => setSearchKeyword('')} style={{ cursor: 'pointer' }} />
                    ) : null
                  }
                />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item label={t('storeSelection.storeStatus')}>
                <Select
                  mode="multiple"
                  placeholder={t('storeSelection.selectStatus')}
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value)}
                  options={STORE_STATUS_OPTIONS}
                  style={{ width: '100%' }}
                  allowClear
                  maxTagCount={2}
                  maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item label={t('storeSelection.format')}>
                <Select
                  mode="multiple"
                  // placeholder={t('storeSelection.selectType')}
                  value={typeFilter}
                  onChange={(value) => setTypeFilter(value)}
                  options={storeTypeOptions}
                  style={{ width: '100%' }}
                  allowClear
                  maxTagCount={2}
                  maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24} style={{ textAlign: 'right', marginTop: '8px' }}>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleResetFilters}
                >
                  {t('common.reset')}
                </Button>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={searching}
                >
                  {t('common.search')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card
        title={t('storeSelection.searchResults')}
        size="small"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            {selectedRowKeys.length > 0 && (
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleBatchAddStores}
              >
                {t('storeSelection.batchAdd', { count: selectedRowKeys.length })}
              </Button>
            )}
          </Space>
        }
      >
        {searching ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : searchResults.length > 0 ? (
          <Table
            columns={searchResultColumns}
            dataSource={searchResults}
            rowKey="id"
            size="small"
            // pagination={{ pageSize: 5 }}
            scroll={{ y: 240 }}
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            }}
          />
        ) : (
          <Empty description={t('storeSelection.noSearchResults')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      <Card
        title={
          <Space>
            <span>{t('storeSelection.selectedStores')}</span>
            <Tag color="blue">{selectedStores.length}</Tag>
          </Space>
        }
        size="small"
      >
        {selectedStores.length > 0 ? (
          <Table
            columns={selectedStoresColumns}
            dataSource={selectedStores}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10 }}
            scroll={{ y: 300 }}
          />
        ) : (
          <Empty description={t('storeSelection.noSelectedStores')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>
    </div>
  );
};

export default StoreSelection;